import { useState } from "react";
import { Plus, Trash2, Settings2, Flag, Braces, FlaskConical } from "lucide-react";

const TYPES = ["string", "integer", "number", "boolean", "object", "array"];

/**
 * Left config rail for the Studio: editable inputs, sample values (preview aid),
 * start step, and the result/outputs mapping. Mirrors the prototype rail.
 */
export default function ConfigRail({
  definition,
  outputsRows,
  editable,
  onStartChange,
  onOutputsChange,
  onInputsChange
}) {
  const inputs = Object.entries(definition?.inputs || {});
  const stepNames = Object.keys(definition?.steps || {});
  const [newInput, setNewInput] = useState("");
  const [sampleValues, setSampleValues] = useState({});

  const setInputType = (name, type) =>
    onInputsChange({
      ...definition.inputs,
      [name]: { ...definition.inputs[name], type }
    });
  const removeInput = (name) => {
    const { [name]: _, ...rest } = definition.inputs || {};
    onInputsChange(rest);
  };
  const addInput = () => {
    const n = newInput.trim();
    if (!n || (definition.inputs || {})[n]) return;
    onInputsChange({ ...(definition.inputs || {}), [n]: { type: "string", required: true } });
    setNewInput("");
  };

  return (
    <div className="w-[280px] shrink-0 border-r border-gray-200 bg-white overflow-auto p-4 space-y-6">
      {/* Inputs */}
      <section>
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
          <Settings2 className="w-3.5 h-3.5 text-gray-400" /> Inputs ({inputs.length})
        </h3>
        <div className="space-y-1.5">
          {inputs.map(([name, spec]) => (
            <div key={name} className="grid grid-cols-[1fr_84px_24px] gap-1 items-center">
              <span className="font-mono text-xs text-gray-800 truncate flex items-center gap-0.5">
                {name}
                {spec.required && <span className="text-red-500">*</span>}
              </span>
              <select
                value={spec.type}
                disabled={!editable}
                onChange={(e) => setInputType(name, e.target.value)}
                className="h-7 text-xs border border-gray-300 rounded px-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
              >
                {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              {editable && (
                <button onClick={() => removeInput(name)} className="p-1 text-gray-400 hover:text-red-600">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
        {editable && (
          <div className="flex gap-1.5 mt-2">
            <input
              value={newInput}
              onChange={(e) => setNewInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addInput()}
              placeholder="input name (e.g. vin)"
              className="flex-1 text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button onClick={addInput} className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        )}
      </section>

      {/* Sample values (preview aid) */}
      {inputs.length > 0 && (
        <section>
          <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
            <FlaskConical className="w-3.5 h-3.5 text-gray-400" /> Sample values
          </h3>
          <p className="text-[11px] text-gray-400 mb-2">
            Not saved — just to preview values as you build.
          </p>
          <div className="space-y-1.5">
            {inputs.map(([name, spec]) => (
              <input
                key={name}
                value={sampleValues[name] ?? ""}
                onChange={(e) => setSampleValues((s) => ({ ...s, [name]: e.target.value }))}
                placeholder={spec.description || name}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            ))}
          </div>
        </section>
      )}

      {/* Start step */}
      <section>
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
          <Flag className="w-3.5 h-3.5 text-gray-400" /> Starts at
        </h3>
        <select
          value={definition?.start_step || ""}
          disabled={!editable}
          onChange={(e) => onStartChange(e.target.value)}
          className="w-full text-xs border border-gray-300 rounded px-2 py-1.5 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
        >
          {stepNames.length === 0 && <option value="">—</option>}
          {stepNames.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </section>

      {/* Result mapping */}
      <section>
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
          <Braces className="w-3.5 h-3.5 text-gray-400" /> Result object
        </h3>
        {!editable && outputsRows.length === 0 && (
          <p className="text-xs text-gray-400">No result mapping.</p>
        )}
        <div className="space-y-2">
          {outputsRows.map((row, i) => (
            <div key={i} className="space-y-1 rounded-lg border border-gray-200 p-2">
              <div className="flex items-center gap-1">
                <input
                  value={row.key}
                  disabled={!editable}
                  onChange={(e) =>
                    onOutputsChange(outputsRows.map((r, idx) => (idx === i ? { ...r, key: e.target.value } : r)))
                  }
                  placeholder="key"
                  className="flex-1 font-mono text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                />
                {editable && (
                  <button
                    onClick={() => onOutputsChange(outputsRows.filter((_, idx) => idx !== i))}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <input
                value={row.value}
                disabled={!editable}
                onChange={(e) =>
                  onOutputsChange(outputsRows.map((r, idx) => (idx === i ? { ...r, value: e.target.value } : r)))
                }
                placeholder="{{ steps.x.response_body.field }}"
                className="w-full font-mono text-[11px] border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
              />
            </div>
          ))}
        </div>
        {editable && (
          <button
            onClick={() => onOutputsChange([...outputsRows, { key: "", value: "" }])}
            className="flex items-center gap-1.5 mt-2 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
          >
            <Plus className="w-4 h-4" /> Add field
          </button>
        )}
      </section>
    </div>
  );
}
